<template>
    <section class="segment-info-panel">
        <h2 class="segment-info-panel__title">
            {{ title }}
        </h2>
        <div class="segment-info-panel__content">
            <hh-form class="segment-info-panel__content__form">
                <hh-accordion-group v-if="hasExtension">
                    <hh-accordion-item class="segment-info-panel__content__editables" title="Main Home" open>
                        <template>
                            <editable-segment-field v-for="item in editingSegment.rootData()"
                                                    :key="item.dottedString"
                                                    class="segment-info-panel__content__editables__editable"
                                                    :value="item"
                                                    :editing="editing"
                                                    :data-manager="dataManager"
                                                    :data-manager-update-key="dataManagerUpdateKey"
                                                    @input="onInput" />
                        </template>
                    </hh-accordion-item>
                    <hh-accordion-item v-for="part in editingSegment.buildingParts()"
                                       :key="part.index"
                                       :title="`Extension ${part.index}`"
                                       :class="`segment-info-panel__content__editables segment-info-panel__content__editables--group segment-info-panel__content__editables--part-${part.index}`">
                        <template>
                            <editable-segment-field v-for="item in part.data"
                                                    :key="item.dottedString"
                                                    class="segment-info-panel__content__editables__editable"
                                                    :value="item"
                                                    :editing="editing"
                                                    :data-manager="dataManager"
                                                    :data-manager-update-key="dataManagerUpdateKey"
                                                    @input="onInput" />
                        </template>
                    </hh-accordion-item>
                </hh-accordion-group>
                <div class="segment-info-panel__content__editables accordion-item" v-else>
                    <div class="accordion-item__content">
                        <editable-segment-field v-for="item in editingSegment.rootData()"
                                                :key="item.dottedString"
                                                class="segment-info-panel__content__editables__editable"
                                                :value="item"
                                                :editing="editing"
                                                :data-manager="dataManager"
                                                :data-manager-update-key="dataManagerUpdateKey"
                                                @input="onInput" />
                    </div>
                </div>
            </hh-form>
        </div>
        <info-panel-buttons class="segment-info-panel__buttons">
            <hh-btn def="home--exit-btn" type="cancel"
                    class="segment-info-panel__button segment-info-panel__button--exit"
                    :text="myResources.btnExit"
                    @click="onExit" />
            <hh-btn v-if="hasErrors"
                    def="home--errors-btn"
                    class="segment-info-panel__button segment-info-panel__button--errors"
                    icon-class="fas fa-circle-exclamation"
                    icon-location="pre"
                    :text="myResources.btnViewErrors"
                    @click="onViewErrors" />
            <hh-btn def="home--save-btn" type="cta"
                    class="segment-info-panel__button segment-info-panel__button--save"
                    icon-class="fas fa-save"
                    icon-location="pre"
                    :track-operation-state="true"
                    :operation-in-progress="saving"
                    :disabled="hasNoEdits"
                    :text="myResources.ctaSave"
                    @click="onSave" />
        </info-panel-buttons>
    </section>
</template>

<script>

import _ from 'lodash'
import Vuex from 'vuex'

import Config from '../../app.config'

import PropertyManager from '../../property-manager'
import PropertyDataRuleManager from '../../property-data-rule-manager'

import Segment from '../../models/segment'

import AnalyticsLogger from '../analytics-logger'
import ResourcesHelper from '@/components/resources-helper'
import EditableSegmentField from './editable-segment-field'
import InfoPanelButtons from './info-panel-buttons'
import DataErrorsPopupModal from '@/components/app-core/modals/data-errors-popup.vue'

const loggingCategory = 'MyHome / Rules'

export default {

    props: {
        segment: {
            type: Segment,
            default: () => {
                return new Segment({})
            },
        },
        editing: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
    },

    mixins: [
        AnalyticsLogger,
        ResourcesHelper,
    ],

    data() {
        return {
            dataManagerObject: null,

            dataManagerUpdateKey: 1,

            editingSegment: _.cloneDeep(this.segment),

            /**
             * @type {string}
             */
            errorMessage: this.error,

            saving: false,
        }
    },

    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {?PropertyAsJson}
             */
            property: stateStore => stateStore.property,

            /**
             * @param {VuexState} stateStore
             * @returns {Array<string>}
             */
            errors: stateStore => stateStore.myHome.errors,

            /**
             * @param {VuexState} stateStore
             * @returns {Array}
             */
            segments: stateStore => stateStore.myHome.segments,

            /**
             * @param {VuexState} stateStore
             * @returns {boolean}
             */
            hasNoEdits: stateStore => (stateStore.myHome.edits || []).filter(edit => edit.value != null)
                .map(edit => edit.value)
                .filter(value => value.initial !== value.edited)
                .length === 0,
        }),

        currentSegmentMapItem() {
            const linkedMapItem = this.segmentMaps.find(s => s.key === this.segment.$key)
            return linkedMapItem ? linkedMapItem : this.segmentMaps[0]
        },

        dataManager() {
            if (!this.dataManagerObject) this._buildDataManager()
            return this.dataManagerObject
        },

        /**
         * @returns {boolean}
         */
        hasErrors() {
            return this.errors && !!this.errors.length
        },

        /**
         * @returns {MyHomeSegmentInfoPanelResources}
         */
        myResources() {
            return this.parseResources('components.myHome.segmentInfoPanel')
        },

        segmentMaps() {
            return this.$scheme.segmentKeymap
        },

        title() {
            return this.currentSegmentMapItem.text
        },

        /**
         * @returns {boolean}
         */
        hasExtension() {
            return this.segment && this.segment.buildingParts().length > 0
        },
    },

    watch: {

        segment(newSegment) {
            this._resetEdits(newSegment)
            PropertyManager.Edits.initialise(newSegment)
        },
    },

    mounted() {
        PropertyManager.Edits.initialise(this.segment)
    },

    methods: {

        _buildDataManager() {
            if (Config.verboseRuleLogging)
                this.$logger.logObject(this.segments, 'Building data manager', loggingCategory)

            if (this.segments.length) {
                this.dataManagerObject = new PropertyDataRuleManager(this.segments
                    .map(segment => _.cloneDeep(segment.data))
                    .reduce((a1, a2) => a1.concat(a2)))
            } else {
                this.$logger.warn('No segments provided to create data manger with', loggingCategory)
            }
        },

        _resetEdits(newSegment = this.segment) {
            PropertyManager.Edits.clear()
            this.editingSegment = _.cloneDeep(newSegment)
            this._buildDataManager()
        },

        onInput() {
            this.dataManagerUpdateKey++
        },

        onExit() {
            this.$router.push({ name: 'MyHome', query: { id: this.property.id } })
        },

        onSave() {
            this._save(false)
        },

        /**
         * Emit the save event, notifying the parent component to save changes to the current
         * segment.
         *
         * @param {boolean} exit If true notify the parent component to move to the next screen after saving.
         */
        _save(exit) {
            this.saving = true
            this.$emit('save', this.segment, exit, () => {
                this.saving = false
            })
        },

        onViewErrors() {
            this.$eventbus.$emit(this.$appevents.showModal, DataErrorsPopupModal, {
                data: { buttonText: 'OK' },
                cssModifier: 'data-errors',
            })
        },
    },

    components: {
        EditableSegmentField,
        InfoPanelButtons,
    },
}

</script>

